import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@environments/environment';
import {
  ListInAppNotificationsParams,
  ListInAppNotificationsResponse,
  MarkInAppNotificationsReadRequest,
  MarkInAppNotificationsReadResponse,
  UnreadCountResponse,
} from '@shared/models/in-app-notification.model';

/**
 * HTTP surface for notification-management in-app notifications.
 *
 * Live push still arrives via Socket.IO (`RealtimeService`); this service
 * covers persistence — history after refresh, authoritative unread count,
 * and durable mark-read.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.notificationManagementApiUrl;

  listNotifications(
    params: ListInAppNotificationsParams = {}
  ): Observable<ListInAppNotificationsResponse> {
    let httpParams = new HttpParams();
    const entries: [string, string | number | undefined][] = [
      ['page', params.page ?? 1],
      ['limit', params.limit ?? 20],
      ['orderBy', params.orderBy ?? 'occurredAt'],
      ['order', params.order ?? 'DESC'],
      ['status', params.status ?? 'all'],
      ['language', params.language],
    ];
    for (const [key, value] of entries) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    }
    return this.http.get<ListInAppNotificationsResponse>(
      `${this.apiUrl}/api/notifications`,
      { params: httpParams }
    );
  }

  getUnreadCount(): Observable<UnreadCountResponse> {
    return this.http.get<UnreadCountResponse>(
      `${this.apiUrl}/api/notifications/unread-count`
    );
  }

  markRead(
    body: MarkInAppNotificationsReadRequest
  ): Observable<MarkInAppNotificationsReadResponse> {
    return this.http.post<MarkInAppNotificationsReadResponse>(
      `${this.apiUrl}/api/notifications/read`,
      body
    );
  }

  markAllRead(): Observable<MarkInAppNotificationsReadResponse> {
    return this.http.post<MarkInAppNotificationsReadResponse>(
      `${this.apiUrl}/api/notifications/read-all`,
      {}
    );
  }
}
